import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import SettingsTabs from '../components/SettingsTabs';
import accountService from '../services/accountService';
import { useT, useLocale } from '../i18n/LanguageContext';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const STATUS_OPTIONS = ['pending', 'completed', 'rejected'];

export default function DeletionRequests() {
  const t = useT();
  const locale = useLocale();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [updatingId, setUpdatingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;

      const res = await accountService.getDeletionRequests(params);
      const data = res.data?.data || res.data;
      setRequests(data.data || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch {
      console.error('Failed to fetch deletion requests');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await accountService.updateDeletionRequest(id, { status });
      await fetchRequests();
    } catch {
      console.error('Failed to update deletion request');
      alert(t('Could not update the request. Please try again.'));
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString(locale, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const statusBadge = (status) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>
      {t(status)}
    </span>
  );

  return (
    <div>
      <PageHeader
        title={t('Account Deletion Requests')}
        subtitle={t('Requests submitted from the public deletion page. Verify and process within 30 days, then mark as completed.')}
      />
      <SettingsTabs />

      {/* Filters */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-[#6C7490] mb-1">{t('Search')}</label>
            <input
              type="text"
              placeholder={t('Email...')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#0089FF]"
            />
          </div>
          <div>
            <label className="block text-xs text-[#6C7490] mb-1">{t('Status')}</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0089FF]"
            >
              <option value="">{t('All Statuses')}</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </select>
          </div>
          <button
            onClick={() => { setFilters({ status: '', search: '' }); setPage(1); }}
            className="px-4 py-2 text-sm text-[#6C7490] hover:text-[#1E293B] border border-[#E2E8F0] rounded-lg"
          >
            {t('Clear')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E2E8F0] flex justify-between items-center">
          <span className="text-sm text-[#6C7490]">{t(total === 1 ? '{total} total request' : '{total} total requests', { total })}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="text-left px-4 py-3 text-[#6C7490] font-medium">{t('Email')}</th>
                <th className="text-left px-4 py-3 text-[#6C7490] font-medium">{t('Requested')}</th>
                <th className="text-left px-4 py-3 text-[#6C7490] font-medium">{t('Status')}</th>
                <th className="text-left px-4 py-3 text-[#6C7490] font-medium">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-8 text-[#6C7490]">{t('Loading...')}</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-[#6C7490]">{t('No deletion requests found')}</td></tr>
              ) : (
                requests.map((req) => (
                  <tr key={req._id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 text-[#1E293B]">{req.email}</td>
                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap text-xs">{formatDate(req.createdAt)}</td>
                    <td className="px-4 py-3">{statusBadge(req.status)}</td>
                    <td className="px-4 py-3">
                      {req.status === 'pending' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateStatus(req._id, 'completed')}
                            disabled={updatingId === req._id}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            {t('Mark Completed')}
                          </button>
                          <button
                            onClick={() => updateStatus(req._id, 'rejected')}
                            disabled={updatingId === req._id}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[#E2E8F0] text-[#b91c1c] hover:bg-red-50 disabled:opacity-50"
                          >
                            {t('Reject')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => updateStatus(req._id, 'pending')}
                          disabled={updatingId === req._id}
                          className="px-3 py-1.5 text-xs text-[#6C7490] hover:text-[#1E293B] border border-[#E2E8F0] rounded-lg disabled:opacity-50"
                        >
                          {t('Reopen')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[#E2E8F0] flex justify-between items-center">
            <span className="text-sm text-[#6C7490]">{t('Page {page} of {totalPages}', { page, totalPages })}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-lg disabled:opacity-50"
              >
                {t('Previous')}
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-lg disabled:opacity-50"
              >
                {t('Next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
